/**
 * @fileoverview Vendor Discovery and Search Hook
 * 
 * Comprehensive vendor search and discovery system for finding qualified vendors
 * based on capabilities, certifications, ratings, and experience.
 * Supports multiple search modes including filtered search, category browsing,
 * featured vendors, and recently active vendors.
 * 
 * Search Capabilities:
 * - Multi-criteria filtering (certifications, ratings, capabilities)
 * - Match scoring algorithm for relevance ranking
 * - Required certification validation (vendors must have ALL required certs)
 * - Enriched results with organization names, capabilities, and certifications
 * 
 * Key Features:
 * - Advanced vendor search with match scoring
 * - Featured vendor recommendations
 * - Category-based vendor browsing
 * - Profile view analytics tracking
 * 
 * Match Scoring Algorithm:
 * Calculates relevance score (0-100+) based on:
 * - Verification status and level (basic/enhanced/premium)
 * - Average rating (0-5 stars)
 * - Experience (completed jobs count)
 * - On-time delivery rate
 * 
 * @module useVendorDiscovery
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { VendorProfileEnhanced } from './useVendorProfileEnhanced';
import { BusinessRulesFilter, getEligibleVerificationLevels } from '@/utils/businessRulesFilter';
import { fetchTestOrganizationIds } from './useTestOrganizations';

/**
 * Filters for vendor search queries
 */
export interface VendorSearchFilters {
  searchQuery?: string;
  location?: string;
  certifications?: string[];
  capabilities?: string[];
  verification_level?: string;
  min_rating?: number;
  min_completed_jobs?: number;
  is_verified?: boolean;
  required_certifications?: string[];
}

/**
 * Extended vendor profile with search-specific data
 */
export interface VendorSearchResult extends VendorProfileEnhanced {
  /** Calculated relevance score (0-100+) */
  match_score?: number;
  /** Display name from organization or business_name field */
  organization_name?: string;
  /** Vendor's capabilities with business percentage */
  vendor_capabilities?: Array<{
    capability_id: string;
    business_percentage: number;
    capability?: { id: string; name: string };
  }>;
  /** Vendor's certifications with verification status */
  vendor_certifications?: Array<{
    certification_name: string;
    verification_status: string;
    certification_types?: { name: string };
  }>;
}

/**
 * Hook for vendor discovery, search, and public profile access
 * 
 * Provides comprehensive vendor search functionality with multiple discovery modes:
 * 
 * 1. **Filtered Search** (`searchVendors`)
 *    - Apply multiple filters simultaneously
 *    - Geographic radius filtering with distance calculation
 *    - Required certifications (vendors must have ALL specified)
 *    - Match scoring for relevance ranking
 * 
 * 2. **Category Browsing** (`getVendorsByCategory`)
 *    - Find verified vendors by business type
 *    - Sorted by rating for quality results
 *    - Limit 20 results for performance
 * 
 * 3. **Featured Vendors** (`getFeaturedVendors`)
 *    - Top-rated, verified vendors
 *    - Dual-sorted by rating and completed jobs
 *    - Ideal for homepage or marketplace showcases
 * 
 * 4. **Recently Active** (`getRecentlyActiveVendors`)
 *    - Vendors with recent profile updates
 *    - Shows currently active businesses
 * 
 * Data Enrichment:
 * All vendor results are automatically enriched with:
 * - Organization names (with fallback to business_name)
 * - Full capability list with categories
 * - Certification details with verification status
 * 
 * Profile Viewing:
 * - `getVendorProfile`: Fetch complete public profile
 * - Automatic profile view analytics logging
 * - Includes certifications and capabilities in result
 * 
 * @returns Object with vendor search and discovery functions
 * 
 * @example
 * ```tsx
 * function VendorSearch() {
 *   const { vendors, loading, searchVendors } = useVendorDiscovery();
 * 
 *   const handleSearch = async () => {
 *     await searchVendors({
 *       location: 'California',
 *       radius: 50, // miles
 *       min_rating: 4.0,
 *       min_completed_jobs: 10,
 *       required_certifications: ['cert-id-1', 'cert-id-2'], // Must have BOTH
 *       is_verified: true
 *     }, {
 *       latitude: 37.7749,
 *       longitude: -122.4194
 *     });
 *   };
 * 
 *   return (
 *     <div>
 *       {vendors.map(vendor => (
 *         <VendorCard
 *           key={vendor.id}
 *           name={vendor.organization_name}
 *           rating={vendor.average_rating}
 *           distance={vendor.distance}
 *           matchScore={vendor.match_score}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Featured vendors for homepage
 * function FeaturedVendors() {
 *   const { vendors, loading, getFeaturedVendors } = useVendorDiscovery();
 * 
 *   useEffect(() => {
 *     getFeaturedVendors();
 *   }, []);
 * 
 *   return <VendorCarousel vendors={vendors} />;
 * }
 * ```
 */
export const useVendorDiscovery = () => {
  const [vendors, setVendors] = useState<VendorSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Helper function to enrich vendors with organization names
  const enrichVendorsWithOrgNames = async (vendors: VendorProfileEnhanced[]): Promise<VendorSearchResult[]> => {
    if (!vendors || vendors.length === 0) return [];
    
    // Try to fetch organization names for vendors that have organization_id
    const vendorIds = vendors.map(v => v.organization_id).filter(Boolean);
    let orgMap = new Map<string, string>();
    
    if (vendorIds.length > 0) {
      const { data: orgs } = await supabase
        .from('organizations')
        .select('id, name')
        .in('id', vendorIds);
      
      orgMap = new Map(orgs?.map(o => [o.id, o.name]) || []);
    }

    // Map organization names to vendors, prioritizing organization name
    return vendors.map(vendor => ({
      ...vendor,
      organization_name: (vendor.organization_id ? orgMap.get(vendor.organization_id) : null) || 
                        vendor.business_name || 
                        'Unknown Organization',
    })) as VendorSearchResult[];
  };

  // Helper function to enrich vendors with capabilities and certifications
  const enrichVendorsWithRelatedData = async (vendors: VendorSearchResult[]): Promise<VendorSearchResult[]> => {
    if (!vendors || vendors.length === 0) return [];
    
    const vendorIds = vendors.map(v => v.id);
    
    // Fetch all capabilities for these vendors
    const { data: allCapabilities } = await supabase
      .from('vendor_capabilities')
      .select(`
        vendor_profile_id,
        capability_id,
        business_percentage,
        capability:capabilities(id, name)
      `)
      .in('vendor_profile_id', vendorIds);
    
    // Fetch all certifications for these vendors
    const { data: allCertifications } = await supabase
      .from('vendor_certifications')
      .select(`
        vendor_profile_id,
        certification_name,
        verification_status,
        certification_types!vendor_certifications_certification_type_id_fkey(name)
      `)
      .in('vendor_profile_id', vendorIds);
    
    // Group by vendor_profile_id
    const capabilitiesMap = (allCapabilities || []).reduce((acc, cap) => {
      if (!acc[cap.vendor_profile_id]) acc[cap.vendor_profile_id] = [];
      acc[cap.vendor_profile_id].push(cap);
      return acc;
    }, {} as Record<string, any[]>);
    
    const certificationsMap = (allCertifications || []).reduce((acc, cert) => {
      if (!acc[cert.vendor_profile_id]) acc[cert.vendor_profile_id] = [];
      acc[cert.vendor_profile_id].push(cert);
      return acc;
    }, {} as Record<string, any[]>);
    
    // Attach to vendors
    return vendors.map(vendor => ({
      ...vendor,
      vendor_capabilities: capabilitiesMap[vendor.id] || [],
      vendor_certifications: certificationsMap[vendor.id] || [],
    }));
  };

  // Search vendors with filters
  const searchVendors = async (
    filters: VendorSearchFilters = {}, 
    referenceLocation?: { latitude: number; longitude: number },
    businessRules?: BusinessRulesFilter
  ) => {
    try {
      setLoading(true);
      setError(null);

      // If filters require capabilities, get qualified vendors first
      let capabilityQualifiedVendorIds: string[] | null = null;
      if (filters.capabilities && filters.capabilities.length > 0) {
        const { data: capableVendors } = await supabase
          .from('vendor_capabilities')
          .select('vendor_profile_id')
          .in('capability_id', filters.capabilities);
        
        if (capableVendors && capableVendors.length > 0) {
          // Get unique vendor IDs that have at least one matching capability
          capabilityQualifiedVendorIds = [...new Set(capableVendors.map(v => v.vendor_profile_id))];
          
          if (capabilityQualifiedVendorIds.length === 0) {
            setVendors([]);
            setLoading(false);
            return;
          }
        } else {
          setVendors([]);
          setLoading(false);
          return;
        }
      }

      // If business rules require certifications, get qualified vendors first
      let certificationQualifiedVendorIds: string[] | null = null;
      const allRequiredCerts = [
        ...(businessRules?.required_certification_ids || []),
        ...(filters.required_certifications || [])
      ];
      
      if (allRequiredCerts.length > 0) {
        const uniqueCerts = [...new Set(allRequiredCerts)];
        const { data: certifiedVendors } = await supabase
          .from('vendor_certifications')
          .select('vendor_profile_id')
          .in('certification_type_id', uniqueCerts)
          .eq('verification_status', 'verified');
        
        if (certifiedVendors && certifiedVendors.length > 0) {
          // Group by vendor_profile_id and count certifications
          const vendorCertCounts = certifiedVendors.reduce((acc, v) => {
            acc[v.vendor_profile_id] = (acc[v.vendor_profile_id] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);
          
          // Filter to vendors that have ALL required certifications
          certificationQualifiedVendorIds = Object.entries(vendorCertCounts)
            .filter(([_, count]) => count === uniqueCerts.length)
            .map(([id, _]) => id);
          
          if (certificationQualifiedVendorIds.length === 0) {
            // No vendors have all required certifications
            setVendors([]);
            setLoading(false);
            return;
          }
        } else {
          // No vendors with any of the required certifications
          setVendors([]);
          setLoading(false);
          return;
        }
      }

      // Fetch test organization IDs to exclude from client-facing results
      const testOrgIds = await fetchTestOrganizationIds();

      let query = supabase
        .from('vendor_profiles_enhanced')
        .select('*');

      // Exclude test organizations from vendor discovery
      if (testOrgIds.length > 0) {
        query = query.not('organization_id', 'in', `(${testOrgIds.join(',')})`);
      }

      // Apply HARD business rules filters first (these cannot be overridden by user filters)
      if (businessRules) {
        // Verification level filter
        if (businessRules.minimum_vendor_verification_level && 
            businessRules.minimum_vendor_verification_level !== 'none') {
          const eligibleLevels = getEligibleVerificationLevels(businessRules.minimum_vendor_verification_level);
          query = query.in('verification_level', eligibleLevels);
        }
        
        // Rating filter
        if (businessRules.minimum_vendor_rating && businessRules.minimum_vendor_rating > 0) {
          query = query.gte('average_rating', businessRules.minimum_vendor_rating);
        }
        
        // Completed jobs filter
        if (businessRules.minimum_vendor_completed_jobs && businessRules.minimum_vendor_completed_jobs > 0) {
          query = query.gte('total_jobs_completed', businessRules.minimum_vendor_completed_jobs);
        }
      }

      // Apply capability filter if we have qualified vendor IDs
      if (capabilityQualifiedVendorIds) {
        query = query.in('id', capabilityQualifiedVendorIds);
      }

      // Apply certification filter if we have qualified vendor IDs
      if (certificationQualifiedVendorIds) {
        // If we also have capability filter, take the intersection
        if (capabilityQualifiedVendorIds) {
          const intersection = certificationQualifiedVendorIds.filter(
            id => capabilityQualifiedVendorIds!.includes(id)
          );
          if (intersection.length === 0) {
            setVendors([]);
            setLoading(false);
            return;
          }
          query = query.in('id', intersection);
        } else {
          query = query.in('id', certificationQualifiedVendorIds);
        }
      }

      // Apply filters
      if (filters.verification_level) {
        query = query.eq('verification_level', filters.verification_level);
      }

      if (filters.is_verified !== undefined) {
        query = query.eq('is_verified', filters.is_verified);
      }

      if (filters.min_rating) {
        query = query.gte('average_rating', filters.min_rating);
      }

      if (filters.min_completed_jobs) {
        query = query.gte('total_jobs_completed', filters.min_completed_jobs);
      }

      // Search vendor names and descriptions
      if (filters.searchQuery) {
        const searchPattern = `%${filters.searchQuery}%`;
        query = query.or(
          `business_name.ilike.${searchPattern},business_description.ilike.${searchPattern}`
        );
      }

      if (filters.location) {
        query = query.or(`country.ilike.%${filters.location}%,state.ilike.%${filters.location}%`);
      }

      // Note: Certification filtering is now handled at the top of the function
      // combining both business rules and user filter certifications

      // Order by verification level and rating
      query = query.order('is_verified', { ascending: false })
                   .order('average_rating', { ascending: false })
                   .order('total_jobs_completed', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      // Enrich with organization names
      let vendorsWithScores = await enrichVendorsWithOrgNames(data || []);

      // Enrich with capabilities and certifications
      vendorsWithScores = await enrichVendorsWithRelatedData(vendorsWithScores);

      // Sort by rating
      vendorsWithScores.sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0));

      setVendors(vendorsWithScores);
    } catch (err: any) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to search vendors",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Get vendor by ID (public profile)
  const getVendorProfile = async (vendorId: string) => {
    try {
      const { data, error } = await supabase
        .from('vendor_profiles_enhanced')
        .select(`
          *,
          vendor_certifications (*),
          vendor_capabilities (*, capability:capabilities(id, name))
        `)
        .eq('id', vendorId)
        .single();

      if (error) throw error;

      // Log profile view
      await logProfileView(vendorId);

      return data;
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to fetch vendor profile",
        variant: "destructive",
      });
      throw err;
    }
  };

  // Get vendors by category
  const getVendorsByCategory = async (category: string, businessRules?: BusinessRulesFilter) => {
    try {
      setLoading(true);
      
      // Handle certification filtering if business rules require it
      let certificationQualifiedVendorIds: string[] | null = null;
      if (businessRules?.required_certification_ids && businessRules.required_certification_ids.length > 0) {
        const { data: certifiedVendors } = await supabase
          .from('vendor_certifications')
          .select('vendor_profile_id')
          .in('certification_type_id', businessRules.required_certification_ids)
          .eq('verification_status', 'verified');
        
        if (certifiedVendors && certifiedVendors.length > 0) {
          const vendorCertCounts = certifiedVendors.reduce((acc, v) => {
            acc[v.vendor_profile_id] = (acc[v.vendor_profile_id] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);
          
          certificationQualifiedVendorIds = Object.entries(vendorCertCounts)
            .filter(([_, count]) => count === businessRules.required_certification_ids!.length)
            .map(([id, _]) => id);
          
          if (certificationQualifiedVendorIds.length === 0) {
            setVendors([]);
            setLoading(false);
            return;
          }
        } else {
          setVendors([]);
          setLoading(false);
          return;
        }
      }
      
      // Fetch test organization IDs to exclude from client-facing results
      const testOrgIds = await fetchTestOrganizationIds();

      let query = supabase
        .from('vendor_profiles_enhanced')
        .select('*')
        .eq('business_type', category)
        .eq('is_verified', true);

      // Exclude test organizations from vendor discovery
      if (testOrgIds.length > 0) {
        query = query.not('organization_id', 'in', `(${testOrgIds.join(',')})`);
      }
      
      // Apply business rules filters
      if (businessRules) {
        if (businessRules.minimum_vendor_verification_level && 
            businessRules.minimum_vendor_verification_level !== 'none') {
          const eligibleLevels = getEligibleVerificationLevels(businessRules.minimum_vendor_verification_level);
          query = query.in('verification_level', eligibleLevels);
        }
        if (businessRules.minimum_vendor_rating && businessRules.minimum_vendor_rating > 0) {
          query = query.gte('average_rating', businessRules.minimum_vendor_rating);
        }
        if (businessRules.minimum_vendor_completed_jobs && businessRules.minimum_vendor_completed_jobs > 0) {
          query = query.gte('total_jobs_completed', businessRules.minimum_vendor_completed_jobs);
        }
      }
      
      if (certificationQualifiedVendorIds) {
        query = query.in('id', certificationQualifiedVendorIds);
      }
      
      query = query.order('average_rating', { ascending: false }).limit(20);

      const { data, error } = await query;

      if (error) throw error;
      
      // Enrich with organization names
      let enrichedVendors = await enrichVendorsWithOrgNames(data || []);
      
      // Enrich with capabilities and certifications
      enrichedVendors = await enrichVendorsWithRelatedData(enrichedVendors);
      setVendors(enrichedVendors);
    } catch (err: any) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to fetch vendors by category",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Get featured/recommended vendors
  const getFeaturedVendors = async (
    referenceLocation?: { latitude: number; longitude: number },
    businessRules?: BusinessRulesFilter
  ) => {
    try {
      setLoading(true);
      
      // Handle certification filtering if business rules require it
      let certificationQualifiedVendorIds: string[] | null = null;
      if (businessRules?.required_certification_ids && businessRules.required_certification_ids.length > 0) {
        const { data: certifiedVendors } = await supabase
          .from('vendor_certifications')
          .select('vendor_profile_id')
          .in('certification_type_id', businessRules.required_certification_ids)
          .eq('verification_status', 'verified');
        
        if (certifiedVendors && certifiedVendors.length > 0) {
          const vendorCertCounts = certifiedVendors.reduce((acc, v) => {
            acc[v.vendor_profile_id] = (acc[v.vendor_profile_id] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);
          
          certificationQualifiedVendorIds = Object.entries(vendorCertCounts)
            .filter(([_, count]) => count === businessRules.required_certification_ids!.length)
            .map(([id, _]) => id);
          
          if (certificationQualifiedVendorIds.length === 0) {
            setVendors([]);
            setLoading(false);
            return;
          }
        } else {
          setVendors([]);
          setLoading(false);
          return;
        }
      }
      
      // Fetch test organization IDs to exclude from client-facing results
      const testOrgIds = await fetchTestOrganizationIds();

      let query = supabase
        .from('vendor_profiles_enhanced')
        .select('*')
        .eq('is_verified', true);

      // Exclude test organizations from vendor discovery
      if (testOrgIds.length > 0) {
        query = query.not('organization_id', 'in', `(${testOrgIds.join(',')})`);
      }
      
      // Apply business rules filters
      if (businessRules) {
        if (businessRules.minimum_vendor_verification_level && 
            businessRules.minimum_vendor_verification_level !== 'none') {
          const eligibleLevels = getEligibleVerificationLevels(businessRules.minimum_vendor_verification_level);
          query = query.in('verification_level', eligibleLevels);
        }
        if (businessRules.minimum_vendor_rating && businessRules.minimum_vendor_rating > 0) {
          query = query.gte('average_rating', businessRules.minimum_vendor_rating);
        }
        if (businessRules.minimum_vendor_completed_jobs && businessRules.minimum_vendor_completed_jobs > 0) {
          query = query.gte('total_jobs_completed', businessRules.minimum_vendor_completed_jobs);
        }
      }
      
      if (certificationQualifiedVendorIds) {
        query = query.in('id', certificationQualifiedVendorIds);
      }
      
      query = query
        .order('average_rating', { ascending: false })
        .order('total_jobs_completed', { ascending: false })
        .limit(12);

      const { data, error } = await query;

      if (error) throw error;
      
      // Enrich with organization names
      let enrichedVendors = await enrichVendorsWithOrgNames(data || []);
      
      // Enrich with capabilities and certifications
      let fullyEnriched = await enrichVendorsWithRelatedData(enrichedVendors);
      
      // Debug log to verify data enrichment
      console.debug('[VendorDiscovery] Featured enriched:', fullyEnriched.map(v => ({ 
        id: v.id, 
        name: v.organization_name,
        caps: v.vendor_capabilities?.length || 0, 
        certs: v.vendor_certifications?.length || 0
      })));
      
      setVendors(fullyEnriched);
    } catch (err: any) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to fetch featured vendors",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Get recently active vendors
  const getRecentlyActiveVendors = async () => {
    try {
      setLoading(true);
      
      // Fetch test organization IDs to exclude from client-facing results
      const testOrgIds = await fetchTestOrganizationIds();

      let query = supabase
        .from('vendor_profiles_enhanced')
        .select('*')
        .eq('is_verified', true);

      // Exclude test organizations from vendor discovery
      if (testOrgIds.length > 0) {
        query = query.not('organization_id', 'in', `(${testOrgIds.join(',')})`);
      }

      const { data, error } = await query
        .order('updated_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      
      // Enrich with organization names
      let enrichedVendors = await enrichVendorsWithOrgNames(data || []);
      
      // Enrich with capabilities and certifications
      enrichedVendors = await enrichVendorsWithRelatedData(enrichedVendors);
      
      setVendors(enrichedVendors);
    } catch (err: any) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to fetch recently active vendors",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Log profile view for analytics
  const logProfileView = async (profileId: string, source: string = 'direct') => {
    try {
      const user = await supabase.auth.getUser();
      
      await supabase
        .from('profile_views')
        .insert({
          viewer_user_id: user.data.user?.id || null,
          viewed_profile_type: 'vendor',
          viewed_profile_id: profileId,
          source: source,
        });
    } catch (err: any) {
      // Don't show error for analytics failures
      console.error('Failed to log profile view:', err);
    }
  };

  // Calculate match score (simple algorithm)
  const calculateMatchScore = (vendor: VendorSearchResult, filters: VendorSearchFilters): number => {
    let score = 0;

    // Base score for verification
    if (vendor.is_verified) score += 20;
    
    // Verification level bonus
    switch (vendor.verification_level) {
      case 'premium': score += 15; break;
      case 'enhanced': score += 10; break;
      case 'basic': score += 5; break;
    }

    // Rating bonus
    score += vendor.average_rating * 5;

    // Experience bonus
    score += Math.min(vendor.total_jobs_completed * 0.1, 10);

    // On-time delivery bonus
    score += vendor.on_time_delivery_rate * 0.1;


    // Location bonus (simplified)
    if (filters.location && vendor.country) {
      const locationMatch = vendor.country.toLowerCase().includes(filters.location.toLowerCase()) ||
                           (vendor.state && vendor.state.toLowerCase().includes(filters.location.toLowerCase()));
      if (locationMatch) score += 10;
    }

    // Capability match bonus
    if (filters.capabilities && filters.capabilities.length > 0 && vendor.vendor_capabilities) {
      const vendorCapabilityIds = vendor.vendor_capabilities.map(vc => vc.capability_id);
      const matchedCapabilities = filters.capabilities.filter(capId => vendorCapabilityIds.includes(capId));
      // Award points based on percentage of requested capabilities matched
      const matchRatio = matchedCapabilities.length / filters.capabilities.length;
      score += Math.round(matchRatio * 20); // Up to 20 bonus points for capability match
    }

    return Math.round(score);
  };


  // Clear search results
  const clearResults = () => {
    setVendors([]);
    setError(null);
  };

  return {
    vendors,
    loading,
    error,
    searchVendors,
    getVendorProfile,
    getVendorsByCategory,
    getFeaturedVendors,
    getRecentlyActiveVendors,
    clearResults,
    logProfileView,
  };
};